import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useVenue } from "../VenueProvider";
import { Section, formatMXN, CustomTooltip, ACCENT } from "../cuentas/shared";
import { Kpi, fmtPct, ultimosMeses, labelMes, COSTO_COLOR } from "./shared";
import {
  variacion,
  estructuraCostos,
  compromisoMensual,
} from "../../utils/reportes";
import { getMonthRange } from "../../utils/dates";
import {
  Squares2X2Icon,
  TableCellsIcon,
  CalendarDaysIcon,
} from "@heroicons/react/20/solid";

// "Si no vendo un solo evento el mes que entra, ¿cuánto me cuesta abrir?"
//
// Tres familias por procedencia del costo: fijo (nació de un gasto
// recurrente), de evento (cuelga de uno) y general (gasto suelto). El
// anclaje es la fecha de causación, no la de pago: esto es la estructura
// del período, no su caja — para la caja está Flujo de caja.
const COLOR_FAMILIA = {
  fijo: "#8B5CF6",
  evento: COSTO_COLOR,
  general: "#F59E0B",
};

const ReporteEstructuraCostos = ({ rango }) => {
  const { costos, gastosRecurrentes, calendarTimezone: tz } = useVenue();

  const actual = useMemo(
    () => estructuraCostos({ costos, desde: rango.from, hasta: rango.to, timeZone: tz }),
    [costos, rango, tz]
  );
  const previo = useMemo(
    () =>
      rango.prev
        ? estructuraCostos({
            costos,
            desde: rango.prev.from,
            hasta: rango.prev.to,
            timeZone: tz,
          })
        : null,
    [costos, rango, tz]
  );

  const compromiso = useMemo(
    () => compromisoMensual(gastosRecurrentes),
    [gastosRecurrentes]
  );

  // Doce meses de estructura: la misma partición, mes a mes, para ver si la
  // carga fija creció sin que nadie lo decidiera.
  const serie = useMemo(() => {
    return ultimosMeses(12).map((mesKey) => {
      const { from, to } = getMonthRange(mesKey);
      const e = estructuraCostos({ costos, desde: from, hasta: to, timeZone: tz });
      return {
        mes: labelMes(mesKey),
        Fijos: e.familias[0].total,
        "De eventos": e.familias[1].total,
        Generales: e.familias[2].total,
      };
    });
  }, [costos, tz]);

  const [fijo, evento, general] = actual.familias;
  const maxCategoria = actual.porCategoria[0]?.total || 0;

  if (actual.total === 0 && compromiso === 0) {
    return (
      <Section icon={Squares2X2Icon} title="Estructura de costos">
        <p className="text-sm text-zinc-400">
          Sin costos capturados en este período. Los gastos generales se
          registran en Cuentas → Balance general, y los que se repiten cada mes
          conviene configurarlos como gasto recurrente para que el panel te los
          proponga solo.
        </p>
      </Section>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Egresos del período"
          value={formatMXN(actual.total)}
          delta={previo ? variacion(actual.total, previo.total) : null}
          invertir
          hint={`${formatMXN(actual.porPagar)} siguen por pagar`}
        />
        <Kpi
          label="Fijos"
          value={formatMXN(fijo.total)}
          delta={previo ? variacion(fijo.total, previo.familias[0].total) : null}
          invertir
          hint="vienen de gastos recurrentes"
        />
        <Kpi
          label="Peso de lo fijo"
          value={fmtPct(actual.pesoFijo)}
          hint="del gasto del período no depende de vender"
        />
        <Kpi
          label="Compromiso mensual"
          value={formatMXN(compromiso)}
          hint="lo que suman al mes tus recurrentes activos"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <Section icon={Squares2X2Icon} title="Composición del gasto">
          <div className="space-y-4">
            {[fijo, evento, general].map((f) => (
              <div key={f.key}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-zinc-600 dark:text-zinc-300">
                    {f.label}
                    <span className="ml-1.5 text-xs text-zinc-400">
                      {f.count} {f.count === 1 ? "costo" : "costos"}
                    </span>
                  </span>
                  <span className="font-medium text-zinc-900 dark:text-white">
                    {formatMXN(f.total)}
                    <span className="ml-1.5 text-xs font-normal text-zinc-400">
                      {actual.total > 0
                        ? `${Math.round((f.total / actual.total) * 100)}%`
                        : "0%"}
                    </span>
                  </span>
                </div>
                <div className="mt-1 h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${actual.total > 0 ? (f.total / actual.total) * 100 : 0}%`,
                      backgroundColor: COLOR_FAMILIA[f.key],
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-zinc-400">
            Fijo es lo que nació de un gasto recurrente, aunque se haya
            confirmado dentro de un evento. Lo demás que cuelga de un evento
            escala con la venta; lo general es gasto suelto del venue.
          </p>
        </Section>

        <Section icon={TableCellsIcon} title="Por categoría">
          {actual.porCategoria.length === 0 ? (
            <p className="text-sm text-zinc-400">Sin costos en el período.</p>
          ) : (
            <div className="space-y-2.5">
              {actual.porCategoria.map((c) => (
                <div key={c.categoria}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="truncate text-zinc-600 dark:text-zinc-300">
                      {c.categoria}
                    </span>
                    <span className="font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                      {formatMXN(c.total)}
                    </span>
                  </div>
                  {/* Una barra, tres tramos: cuánto de esa categoría es fijo,
                      de evento y general. Es donde se ve que "Servicios" son
                      en realidad tres cosas distintas. */}
                  <div className="mt-1 flex h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-700/60">
                    {["fijo", "evento", "general"].map((k) =>
                      c[k] > 0 ? (
                        <div
                          key={k}
                          style={{
                            width: `${maxCategoria > 0 ? (c[k] / maxCategoria) * 100 : 0}%`,
                            backgroundColor: COLOR_FAMILIA[k],
                          }}
                        />
                      ) : null
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section icon={CalendarDaysIcon} title="Estructura mes a mes">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={serie} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatMXN(v)} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Fijos" stackId="a" fill={COLOR_FAMILIA.fijo} />
            <Bar dataKey="De eventos" stackId="a" fill={COLOR_FAMILIA.evento} />
            <Bar dataKey="Generales" stackId="a" fill={COLOR_FAMILIA.general} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="mt-2 text-xs text-zinc-400">
          Los doce meses no se acotan al período de arriba: la carga fija se
          lee en su tendencia, no en una foto.{" "}
          <span style={{ color: ACCENT }}>
            Configura tus recurrentes en Cuentas → Balance general.
          </span>
        </p>
      </Section>
    </div>
  );
};

export default ReporteEstructuraCostos;
